import { css } from '@emotion/react';
import ThumbnailBullets from 'components/_shared/widgets/thumbnail-bullets';
import { Box, Flex, Grid } from 'components/box';
import dynamic from 'next/dynamic';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { breakpointsPx } from 'themes/onedayonly';
import { Text } from 'components/typography';
import Button from 'components/button';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { File, SizeChart } from 'types/types';
import RichText from 'components/rich-text';
import {
  EventNamesEnum,
  SizeGuideInteractionEvent,
} from 'types/third-party/gtm';
import { trackSizeGuideInteraction } from '../../../../util/tag-manager';

const ModalWithClose = dynamic(
  () => import('components/_shared/widgets/modal/with-close')
);

const ResponsiveImage = ({
  mobileImage,
  tabletImage,
  desktopImage,
}: {
  mobileImage?: File;
  tabletImage?: File;
  desktopImage: File;
}) => (
  <Flex width="100%" justifyContent="center" height="100%" alignItems="center">
    <picture>
      {mobileImage && (
        <source
          srcSet={mobileImage.url}
          media={`(max-width: ${breakpointsPx[0] - 1}px)`}
        />
      )}
      {tabletImage && (
        <source
          srcSet={tabletImage.url}
          media={`(min-width: ${breakpointsPx[0]}px) and (max-width: ${
            breakpointsPx[1] - 1
          }px)`}
        />
      )}
      <img
        src={desktopImage.url}
        alt={desktopImage.label}
        css={theme => css`
          width: 100%;
          margin: 0;
          padding: 0;
          @media ${theme.mediaQueries.desktopUp} {
            max-height: 600px;
          }
        `}
      />
    </picture>
  </Flex>
);

const SizeChartModal = ({
  close,
  isOpen,
  sizeChart,
  sizeChartImage,
}: {
  close: () => void;
  isOpen: boolean;
  sizeChart: SizeChart | null;
  sizeChartImage?: File;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const gtmTrackerRef = useRef<
    Record<number, { event: SizeGuideInteractionEvent; tracked: boolean }>
  >({});

  const handleClose = () => {
    setActiveIndex(0);
    close();
  };

  useEffect(() => {
    if (isOpen) {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        containerRef.current.scrollTo({
          left: activeIndex * width,
          behavior: 'smooth',
        });
      }
      if (
        gtmTrackerRef.current[activeIndex] &&
        !gtmTrackerRef.current[activeIndex].tracked
      ) {
        gtmTrackerRef.current[activeIndex].tracked = true;
        trackSizeGuideInteraction(gtmTrackerRef.current[activeIndex].event);
      }
    }
  }, [activeIndex, isOpen]);

  const slides: ReactNode[] = useMemo(() => {
    const slides: ReactNode[] = [];
    let trackerIndex = 0;

    if (sizeChart?.desktopImage) {
      gtmTrackerRef.current[trackerIndex] = {
        event: EventNamesEnum.viewSizeImage,
        tracked: false,
      };
      trackerIndex++;
      slides.push(
        <ResponsiveImage
          desktopImage={sizeChart.desktopImage}
          tabletImage={sizeChart.tabletImage}
          mobileImage={sizeChart.mobileImage}
        />
      );
    }

    if (sizeChart?.howToMeasure?.desktopImage) {
      gtmTrackerRef.current[trackerIndex] = {
        event: EventNamesEnum.viewSizeHowToMeasure,
        tracked: false,
      };
      slides.push(
        <ResponsiveImage
          desktopImage={sizeChart.howToMeasure.desktopImage}
          tabletImage={sizeChart.howToMeasure.tabletImage}
          mobileImage={sizeChart.howToMeasure.mobileImage}
        />
      );
    }

    return slides;
  }, [sizeChart]);

  const showNavigation = slides.length > 1;

  return (
    <ModalWithClose
      title="Size Guide"
      close={handleClose}
      isOpen={isOpen}
      size="small"
    >
      {sizeChart?.recommendation && (
        <Box mx="auto" width="fit-content" textAlign="center">
          <Text fontWeight="bold" fontSize={[2, 3]} mb="8px">
            Our Recommendation:
          </Text>
          <RichText
            color="purple"
            mb="10px"
            content={sizeChart.recommendation}
          />
        </Box>
      )}

      {sizeChart && (
        <>
          <Grid gridTemplateColumns={showNavigation ? 'auto 1fr auto' : '1fr'}>
            {showNavigation && (
              <Button
                variant="flat"
                color="darkGrey"
                borderRadius="50%"
                p="2px"
                shadow
                height="fit-content"
                width="fit-content"
                my="auto"
                onClick={() => setActiveIndex(activeIndex - 1)}
                disabled={activeIndex === 0}
              >
                <ChevronLeft />
              </Button>
            )}

            <Grid
              ref={containerRef}
              overflow="hidden"
              gridTemplateColumns={`repeat(${slides.length}, 100%)`}
              gridColumnGap={0}
            >
              {slides.map((slide, idx: number) => (
                <Box key={idx}>{slide}</Box>
              ))}
            </Grid>

            {showNavigation && (
              <Button
                variant="flat"
                color="darkGrey"
                borderRadius="50%"
                p="2px"
                shadow
                height="fit-content"
                width="fit-content"
                my="auto"
                onClick={() => setActiveIndex(activeIndex + 1)}
                disabled={activeIndex === slides.length - 1}
              >
                <ChevronRight />
              </Button>
            )}
          </Grid>

          {showNavigation && (
            <ThumbnailBullets
              total={slides.length}
              activeIndex={activeIndex}
              showOnAllDevices
              size="small"
            />
          )}
        </>
      )}

      {!sizeChart && sizeChartImage && (
        <ResponsiveImage desktopImage={sizeChartImage} />
      )}
    </ModalWithClose>
  );
};

export default SizeChartModal;
